// frontend/src/components/Register.js
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import { extractApiError } from '../utils/apiError';

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.register.passwordMismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post('/register', { username, email, password });
      if (response.status === 201) {
        setUsername('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        navigate('/login', {
          replace: true,
          state: { successMessage: t('auth.login.successAfterRegister') },
        });
      }
    } catch (err) {
      setError(extractApiError(err, t('auth.register.registrationFailed')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      eyebrow={t('auth.register.eyebrow')}
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('auth.register.heading')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(223, 232, 241, 0.68)' }}>
            {t('auth.register.body')}
          </Typography>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label={t('common.username')}
              placeholder={t('auth.register.usernamePlaceholder')}
              fullWidth
              required
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  backgroundColor: 'rgba(8, 15, 28, 0.72)',
                },
              }}
            />
            <TextField
              label={t('common.email')}
              placeholder={t('auth.register.emailPlaceholder')}
              type="email"
              fullWidth
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  backgroundColor: 'rgba(8, 15, 28, 0.72)',
                },
              }}
            />
            <TextField
              label={t('common.password')}
              placeholder={t('auth.register.passwordPlaceholder')}
              type="password"
              fullWidth
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText={t('auth.register.passwordHelp')}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  backgroundColor: 'rgba(8, 15, 28, 0.72)',
                },
              }}
            />
            <TextField
              label={t('auth.register.confirmPassword')}
              placeholder={t('auth.register.confirmPasswordPlaceholder')}
              type="password"
              fullWidth
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  backgroundColor: 'rgba(8, 15, 28, 0.72)',
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              fullWidth
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #F5C36B, #F39A64)',
                color: '#111827',
                '&:hover': {
                  background: 'linear-gradient(135deg, #f7ce85, #f5ab7d)',
                },
              }}
            >
              {isSubmitting ? <CircularProgress size={22} sx={{ color: '#111827' }} /> : t('auth.register.submit')}
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(126, 144, 168, 0.16)' }} />

        <Typography variant="body2" sx={{ color: 'rgba(223, 232, 241, 0.68)' }}>
          {t('auth.register.hasAccount')}{' '}
          <MuiLink component={Link} to="/login" underline="hover" color="#58C4DD">
            {t('auth.register.loginLink')}
          </MuiLink>
        </Typography>
      </Stack>
    </AuthShell>
  );
};

export default Register;
